import { useEffect, useRef } from 'react';

// User-defined keyboard shortcuts (Settings → Keybinds). Stored as e.g. "Ctrl+Shift+M"; synced with the account settings.
export type KeybindAction = 'toggle_mute' | 'toggle_deafen' | 'push_to_talk' | 'disconnect' | 'toggle_camera' | 'toggle_screen' | 'soundboard';
export const KEYBIND_ACTIONS: { id: KeybindAction; en: string; de: string; hintEn: string; hintDe: string }[] = [
  { id: 'toggle_mute', en: 'Toggle mute', de: 'Stummschalten an/aus', hintEn: 'Microphone on/off', hintDe: 'Mikrofon an/aus' },
  { id: 'toggle_deafen', en: 'Toggle deafen', de: 'Ton aus an/aus', hintEn: 'Hear nothing and mute yourself', hintDe: 'Nichts hören und stumm sein' },
  { id: 'push_to_talk', en: 'Push to talk', de: 'Push-to-Talk', hintEn: 'Microphone is only on while you hold the key', hintDe: 'Mikrofon nur an, solange die Taste gedrückt ist' },
  { id: 'disconnect', en: 'Leave call', de: 'Anruf verlassen', hintEn: 'Disconnect from voice', hintDe: 'Sprachkanal verlassen' },
  { id: 'toggle_camera', en: 'Toggle camera', de: 'Kamera an/aus', hintEn: 'In a call', hintDe: 'Im Anruf' },
  { id: 'toggle_screen', en: 'Toggle screen share', de: 'Bildschirm teilen an/aus', hintEn: 'In a call', hintDe: 'Im Anruf' },
  { id: 'soundboard', en: 'Open soundboard', de: 'Soundboard öffnen', hintEn: 'In a call', hintDe: 'Im Anruf' }
];
const KEY = 'malvo-keybinds';
export function loadKeybinds(): Partial<Record<KeybindAction, string>> {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch { return {}; }
}
export function saveKeybinds(map: Partial<Record<KeybindAction, string>>) {
  try { localStorage.setItem(KEY, JSON.stringify(map)); } catch {}
  window.dispatchEvent(new Event('malvo-keybinds-changed'));
}
const MOD = new Set(['Control', 'Shift', 'Alt', 'Meta']);
// "Ctrl+Shift+M" style name for a key event; null while only modifiers are held.
export function comboFromEvent(e: KeyboardEvent | React.KeyboardEvent): string | null {
  // Browser autofill and password managers dispatch keydown events without key/code.
  if (typeof e.code !== 'string' || !e.code || MOD.has(e.key)) return null;
  const parts: string[] = [];
  if (e.ctrlKey) parts.push('Ctrl');
  if (e.altKey) parts.push('Alt');
  if (e.shiftKey) parts.push('Shift');
  if (e.metaKey) parts.push('Meta');
  const k = e.code.startsWith('Key') ? e.code.slice(3) : e.code.startsWith('Digit') ? e.code.slice(5) : e.code.startsWith('Numpad') ? 'Num' + e.code.slice(6) : e.code === 'Backquote' ? '`' : e.code;
  parts.push(k);
  return parts.join('+');
}
// Electron accelerator syntax for global shortcuts in the desktop app.
export function toAccelerator(combo: string) {
  return combo.split('+').map(p => (p === 'Ctrl' ? 'CommandOrControl' : p === 'Meta' ? 'Super' : p === 'ArrowUp' ? 'Up' : p === 'ArrowDown' ? 'Down' : p === 'ArrowLeft' ? 'Left' : p === 'ArrowRight' ? 'Right' : p.startsWith('Num') ? 'num' + p.slice(3) : p)).join('+');
}
const typing = (t: EventTarget | null) => { const el = t as HTMLElement | null; return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable); };

export function useKeybinds(handlers: { run: (a: KeybindAction) => void; pushToTalk: (down: boolean) => void }) {
  const ref = useRef(handlers); ref.current = handlers;
  useEffect(() => {
    let binds = loadKeybinds();
    const reload = () => { binds = loadKeybinds(); syncDesktop(binds); };
    const pressed = new Set<string>();
    const down = (e: KeyboardEvent) => {
      const combo = comboFromEvent(e); if (!combo) return;
      const plain = !combo.includes('+');
      if (plain && typing(e.target)) return; // single letters while typing stay text
      for (const [action, key] of Object.entries(binds) as [KeybindAction, string][]) {
        if (key !== combo) continue;
        e.preventDefault();
        if (action === 'push_to_talk') { if (!pressed.has(combo)) { pressed.add(combo); ref.current.pushToTalk(true); } }
        else if (!e.repeat) ref.current.run(action);
      }
    };
    const up = (e: KeyboardEvent) => {
      const ptt = binds.push_to_talk; if (!ptt || typeof e.code !== 'string') return;
      const last = ptt.split('+').pop();
      if (!e.code) return;
      const k = e.code.startsWith('Key') ? e.code.slice(3) : e.code.startsWith('Digit') ? e.code.slice(5) : e.code;
      if (pressed.has(ptt) && (k === last || MOD.has(e.key))) { pressed.delete(ptt); ref.current.pushToTalk(false); }
    };
    const blur = () => { if (pressed.size) { pressed.clear(); ref.current.pushToTalk(false); } };
    const desktop = (window as any).malvoDesktop;
    const offDesktop = desktop?.onKeybind?.((action: KeybindAction) => { if (action !== 'push_to_talk') ref.current.run(action); });
    syncDesktop(binds);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    window.addEventListener('malvo-keybinds-changed', reload);
    window.addEventListener('storage', reload);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); window.removeEventListener('blur', blur); window.removeEventListener('malvo-keybinds-changed', reload); window.removeEventListener('storage', reload); offDesktop?.(); };
  }, []);
}
// Desktop app: the same shortcuts also work while MALVO is in the background (not push-to-talk).
function syncDesktop(binds: Partial<Record<KeybindAction, string>>) {
  const desktop = (window as any).malvoDesktop;
  if (!desktop?.setKeybinds) return;
  const map: Record<string, string> = {};
  for (const [a, k] of Object.entries(binds)) if (k && a !== 'push_to_talk' && k.includes('+')) map[a] = toAccelerator(k);
  desktop.setKeybinds(map);
}
